// Job Role Analysis management functions
// This is a shared resource accessible to all users
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore'
import { db } from './config'

const JOB_ROLE_ANALYSIS_COLLECTION = 'jobRoleAnalysis'

/**
 * Create a new job role analysis entry
 * @param {Object} jobData - Job data (jobTitle, company, source, category, skills, certifications, responsibilities, notes)
 * @returns {Promise<string>} Document ID
 */
export const createJobRoleAnalysis = async (jobData) => {
  try {
    const docRef = await addDoc(collection(db, JOB_ROLE_ANALYSIS_COLLECTION), {
      ...jobData,
      skills: jobData.skills || [],
      certifications: jobData.certifications || [],
      responsibilities: jobData.responsibilities || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return docRef.id
  } catch (error) {
    console.error('Error creating job role analysis:', error)
    throw error
  }
}

/**
 * Update an existing job role analysis entry
 * @param {string} jobId - Job role analysis document ID
 * @param {Object} updates - Fields to update
 */
export const updateJobRoleAnalysis = async (jobId, updates) => {
  try {
    const docRef = doc(db, JOB_ROLE_ANALYSIS_COLLECTION, jobId)
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    })
  } catch (error) {
    console.error('Error updating job role analysis:', error)
    throw error
  }
}

/**
 * Delete a job role analysis entry
 * @param {string} jobId - Job role analysis document ID
 */
export const deleteJobRoleAnalysis = async (jobId) => {
  try {
    const jobRef = doc(db, JOB_ROLE_ANALYSIS_COLLECTION, jobId)
    await deleteDoc(jobRef)
  } catch (error) {
    console.error('Error deleting job role analysis:', error)
    throw error
  }
}

/**
 * Get all job role analysis entries
 * @returns {Promise<Array>} Array of job role analysis entries
 */
export const getJobRoleAnalysis = async () => {
  try {
    const q = query(
      collection(db, JOB_ROLE_ANALYSIS_COLLECTION),
      orderBy('createdAt', 'desc')
    )
    
    const querySnapshot = await getDocs(q)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
  } catch (error) {
    console.error('Error getting job role analysis:', error)
    throw error
  }
}

/**
 * Subscribe to job role analysis changes (real-time)
 * This is a shared resource, so no user filtering
 * @param {Function} callback - Callback function
 * @returns {Function} Unsubscribe function
 */
export const subscribeToJobRoleAnalysis = (callback) => {
  const q = query(
    collection(db, JOB_ROLE_ANALYSIS_COLLECTION),
    orderBy('createdAt', 'desc')
  )
  
  return onSnapshot(q, (snapshot) => {
    const jobs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }))
    callback(jobs)
  }, (error) => {
    console.error('Error subscribing to job role analysis:', error)
  })
}
